import { useMemo, useState } from 'react';
import { Thumb } from '../components/ImagePick';
import { courses as CourseApi, lessons as LessonApi, type Course, type Lesson } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { CourseForm } from './CourseForm';
import { LessonForm } from './LessonForm';

const runtime = (sec: number) => {
  if (!sec) return '—';
  const m = Math.round(sec / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

export function Learning() {
  const courses = useAsync((signal) => CourseApi.list(signal));
  const lessons = useAsync((signal) => LessonApi.list(signal));
  const [openCourse, setOpenCourse] = useState<number | null>(null);
  const [editingCourse, setEditingCourse] = useState<Course | 'new' | null>(null);
  const [editingLesson, setEditingLesson] = useState<Lesson | { course_id: number } | null>(null);

  const byCourse = useMemo(() => {
    const map = new Map<number, Lesson[]>();
    for (const l of lessons.data?.items ?? []) {
      map.set(l.course_id, [...(map.get(l.course_id) ?? []), l]);
    }
    for (const list of map.values()) list.sort((a, b) => a.sort - b.sort || a.id - b.id);
    return map;
  }, [lessons.data]);

  const reloadBoth = async () => { await Promise.all([courses.reload(), lessons.reload()]); };
  const items = courses.data?.items ?? [];

  /* Two selections, because a module and a lesson are not the same kind of
     thing and publishing one does not publish the other. Lesson ids are unique
     across every module, so one lesson selection spans all the open tables. */
  const csel = useSelection(items);
  const lsel = useSelection(lessons.data?.items ?? []);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Recorded learning</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {courses.loading && !courses.data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : `${items.length} module${items.length === 1 ? '' : 's'} · ${lessons.data?.items.length ?? 0} lessons`}
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditingCourse('new')}>New module</button>
      </div>

      {courses.error ? <div className="err">Could not load the modules — {courses.error.message}</div> : null}

      {items.length === 0 && !courses.loading ? (
        <div className="crd" style={{ padding: '40px 32px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--fd)', fontSize: 19, marginBottom: 8 }}>No modules yet</div>
          <p className="muted" style={{ fontSize: 13.5 }}>
            A module holds the lessons. Published modules appear on the recorded-learning page.
          </p>
        </div>
      ) : null}

      <BulkActions
        path="/courses" noun="module"
        rows={csel.picked.map((c) => ({ id: c.id, label: String(c.title) }))}
        actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
        onDone={async () => { csel.clear(); await reloadBoth(); }}
      />
      <BulkActions
        path="/lessons" noun="lesson"
        rows={lsel.picked.map((l) => ({ id: l.id, label: String(l.title) }))}
        actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
        onDone={async () => { lsel.clear(); await reloadBoth(); }}
      />

      <div style={{ display: 'grid', gap: 14 }}>
        {items.map((c) => {
          const mine = byCourse.get(c.id) ?? [];
          const open = openCourse === c.id;
          return (
            <div key={c.id} className="crd" style={{ padding: '18px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <input type="checkbox" checked={csel.isOn(c.id)} aria-label={`Select ${c.title}`}
                  onChange={(e) => csel.toggle(c.id, e.target.checked)} />
                <button className="b bo" type="button" style={{ padding: '4px 10px', fontSize: 14, lineHeight: 1 }}
                  aria-expanded={open} onClick={() => setOpenCourse(open ? null : c.id)}>{open ? '−' : '+'}</button>
                <Thumb src={c.cover_path} width={84} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontFamily: 'var(--fd)', fontSize: 17, fontWeight: 600 }}>{c.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--tx3)', marginTop: 3 }}>
                    {c.track} · {c.lesson_count} lesson{c.lesson_count === 1 ? '' : 's'} · {runtime(c.runtime_sec)}
                  </div>
                </div>
                <span className="tg" style={{
                  background: c.state === 'published' ? 'rgba(62,207,142,.14)' : 'rgba(var(--wash), calc(.06 * var(--washx)))',
                }}>{c.state}</span>
                <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                  type="button" onClick={() => setEditingCourse(c)}>Edit module</button>
                <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                  type="button" onClick={() => setEditingLesson({ course_id: c.id })}>Add lesson</button>
              </div>

              {open ? (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
                  {mine.length === 0 ? (
                    <p className="muted" style={{ fontSize: 13 }}>No lessons in this module yet.</p>
                  ) : (
                    <table className="tbl">
                      <thead>
                        <tr>
                          <TickAll on={mine.every((l) => lsel.isOn(l.id))}
                            some={mine.some((l) => lsel.isOn(l.id))}
                            onChange={(v) => lsel.toggleMany(mine.map((l) => l.id), v)} />
                          <th>#</th><th>Lesson</th><th>Length</th><th>Access</th><th>State</th><th /></tr>
                      </thead>
                      <tbody>
                        {mine.map((l, i) => (
                          <tr key={l.id} style={lsel.isOn(l.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                            <Tick on={lsel.isOn(l.id)} label={String(l.title)}
                              onChange={(v) => lsel.toggle(l.id, v)} />
                            <td className="muted">{i + 1}</td>
                            <td>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <Thumb src={l.thumb_path} width={48} />
                                <div>
                                  <div>{l.title}</div>
                                  {!l.video_url ? (
                                    <div style={{ fontSize: 11, color: 'var(--down)' }}>No video linked yet</div>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                            <td className="muted">{runtime(l.duration_sec)}</td>
                            <td className="muted">{l.access === 'Free' ? 'Free preview' : 'Buyers only'}</td>
                            <td className="muted">{l.state}</td>
                            <td className="right">
                              <button className="b bo" style={{ padding: '5px 10px', fontSize: 11.5 }}
                                type="button" onClick={() => setEditingLesson(l)}>Edit</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {editingCourse ? (
        <CourseForm
          course={editingCourse === 'new' ? null : editingCourse}
          onCancel={() => setEditingCourse(null)}
          onSaved={async () => { setEditingCourse(null); await reloadBoth(); }}
        />
      ) : null}

      {editingLesson ? (
        <LessonForm
          lesson={'id' in editingLesson ? editingLesson : null}
          courseId={'id' in editingLesson ? editingLesson.course_id : editingLesson.course_id}
          courses={items}
          nextSort={(byCourse.get('id' in editingLesson ? editingLesson.course_id : editingLesson.course_id)?.length ?? 0) + 1}
          onCancel={() => setEditingLesson(null)}
          onSaved={async () => { setEditingLesson(null); await reloadBoth(); }}
        />
      ) : null}
    </div>
  );
}
